import { ReloadOutlined, SearchOutlined } from '@ant-design/icons';
import type { MaterialRow } from '@hq/api-sdk';
import { ApiError } from '@hq/api-sdk';
import { formatAmount } from '@hq/utils';
import { keepPreviousData, useInfiniteQuery } from '@tanstack/react-query';
import { Alert, Button, Card, Descriptions, Drawer, Input, Space, Table, Tag, Tooltip, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useState } from 'react';
import { hq } from '../api/client';
import { ellipsisCell, sumWidth } from '@hq/ui-kit';

const PAGE_SIZE = 20;

/** 物资主数据：只读列表 + 详情抽屉（wl_material）。 */
export default function MaterialList() {
  const [keyword, setKeyword] = useState('');
  const [search, setSearch] = useState('');
  const [current, setCurrent] = useState<MaterialRow | null>(null);

  const query = useInfiniteQuery({
    queryKey: ['material', 'list', search],
    placeholderData: keepPreviousData,
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) => hq.material.list({ lastId: pageParam, size: PAGE_SIZE, keyword: search || undefined }),
    getNextPageParam: (lastPage) => (lastPage.hasMore ? lastPage.nextLastId : undefined)
  });

  const rows = query.data?.pages.flatMap((page) => page.items) ?? [];

  const columns: ColumnsType<MaterialRow> = [
    { title: '物资编码', dataIndex: 'materialCode', width: 150, render: (value: string | null) => value || '-' },
    {
      title: '物资名称',
      dataIndex: 'materialName',
      ellipsis: { showTitle: false },
      render: (value: string | null, row) => (
        <Tooltip title={value || '-'} placement="topLeft">
          <Space size={4} align="center">
            <Typography.Link onClick={() => setCurrent(row)}>{value || '-'}</Typography.Link>
            {row.isEmergency ? <Tag color="red" style={{ marginInlineEnd: 0 }}>急抢救</Tag> : null}
          </Space>
        </Tooltip>
      )
    },
    { title: '别名', dataIndex: 'alias', width: 140, ellipsis: { showTitle: false }, render: ellipsisCell('alias') },
    { title: '品牌', dataIndex: 'brand', width: 120, render: (value: string | null) => value || '-' },
    { title: '规格', dataIndex: 'spec', width: 130, render: (value: string | null) => value || '-' },
    {
      title: '参考价',
      dataIndex: 'referencePrice',
      width: 120,
      align: 'right',
      render: (value: number) => formatAmount(value, 4)
    },
    { title: '管理类型', dataIndex: 'manageType', width: 120, render: (value: string | null) => value || '-' },
    { title: '管控级别', dataIndex: 'controlLevel', width: 110, render: (value: string | null) => value || '-' },
    {
      title: '审核状态',
      dataIndex: 'auditStatus',
      width: 110,
      render: (value: string | null) =>
        value === 'APPROVED' ? <Tag color="green">已审核</Tag> : <Tag color="gold">{value ?? '待审核'}</Tag>
    },
    { title: '默认来源', dataIndex: 'defaultSourceMode', width: 120, render: (value: string | null) => value || '-' },
    {
      title: '医疗器械',
      dataIndex: 'isMedicalDevice',
      width: 100,
      render: (value: boolean) => (value ? <Tag color="blue">是</Tag> : '-')
    }
  ];

  return (
    <Card
      className="page-card"
      title="物资资料"
      extra={
        <Button icon={<ReloadOutlined />} onClick={() => query.refetch()} loading={query.isFetching}>
          刷新
        </Button>
      }
    >
      <Space wrap style={{ marginBottom: 12 }}>
        <Input
          allowClear
          prefix={<SearchOutlined />}
          placeholder="物资编码 / 名称 / 拼音码"
          value={keyword}
          onChange={(event) => setKeyword(event.target.value)}
          onPressEnter={() => setSearch(keyword.trim())}
          style={{ width: 280 }}
        />
        <Button type="primary" onClick={() => setSearch(keyword.trim())}>
          查询
        </Button>
      </Space>

      {query.isError ? (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 12 }}
          message="物资资料加载失败"
          description={query.error instanceof ApiError ? query.error.friendlyMessage : String(query.error)}
        />
      ) : null}

      <Table<MaterialRow>
        rowKey="id"
        size="middle"
        columns={columns}
        dataSource={rows}
        loading={query.isPending}
        pagination={false}
        scroll={{ x: sumWidth(columns) }}
        locale={{ emptyText: query.isError ? '加载失败，请重试' : '暂无物资' }}
        onRow={(row) => ({ onDoubleClick: () => setCurrent(row) })}
      />

      <Space style={{ marginTop: 16 }}>
        <Typography.Text type="secondary">已加载 {rows.length} 条</Typography.Text>
        {query.hasNextPage ? (
          <Button onClick={() => query.fetchNextPage()} loading={query.isFetchingNextPage}>
            加载更多
          </Button>
        ) : rows.length > 0 ? (
          <Typography.Text type="secondary">没有更多了</Typography.Text>
        ) : null}
      </Space>

      <Drawer
        title={current?.materialName ?? '物资详情'}
        open={current !== null}
        onClose={() => setCurrent(null)}
        width={560}
      >
        {current ? (
          <Descriptions column={1} size="small" bordered>
            <Descriptions.Item label="物资编码">{current.materialCode || '-'}</Descriptions.Item>
            <Descriptions.Item label="别名">{current.alias || '-'}</Descriptions.Item>
            <Descriptions.Item label="品牌 / 型号">
              {[current.brand, current.model].filter(Boolean).join(' / ') || '-'}
            </Descriptions.Item>
            <Descriptions.Item label="规格">{current.spec || '-'}</Descriptions.Item>
            <Descriptions.Item label="参考价">{formatAmount(current.referencePrice, 4)}</Descriptions.Item>
            <Descriptions.Item label="管理类型">{current.manageType || '-'}</Descriptions.Item>
            <Descriptions.Item label="管控级别">{current.controlLevel || '-'}</Descriptions.Item>
            <Descriptions.Item label="审核状态">{current.auditStatus || '-'}</Descriptions.Item>
            <Descriptions.Item label="默认来源">{current.defaultSourceMode || '-'}</Descriptions.Item>
            <Descriptions.Item label="医疗器械">{current.isMedicalDevice ? '是' : '否'}</Descriptions.Item>
            <Descriptions.Item label="急抢救">{current.isEmergency ? '是' : '否'}</Descriptions.Item>
            <Descriptions.Item label="备注">{current.remark || '-'}</Descriptions.Item>
          </Descriptions>
        ) : null}
      </Drawer>
    </Card>
  );
}
